import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { Box } from "@gemini/core";
import { CloseIcon, InfoFilledCircleIcon } from "@gemini/icons";
import { Button, RadioGroup, useSnackbar } from "@gemini/ui";
import { Controller, useForm } from "react-hook-form";
import { OptionLabel, OptionValue } from "./types";
import { useI18n } from "@/shared/hooks";
import { fetchWithTimeout } from "@/utils";
import { FEEDBACK_SURVEY_PATH } from "@/constants/api";
import { useBrandUrl } from "@/hooks";
export const FloorplanSurvey = ({ mediaID }) => {
    const { t } = useI18n();
    const [isOpen, setIsOpen] = useState(false);
    const handleOpen = () => {
        setIsOpen(true);
    };
    return (_jsx(Box, { minHeight: "sizing.64", backgroundColor: "color.surface.status.information.default", position: "absolute", right: "0", top: "spacing.40", paddingHorizontal: "spacing.16", paddingVertical: isOpen ? "spacing.24" : "spacing.12", display: {
            "breakpoint.base": "none",
            "breakpoint.md": "flex",
        }, alignItems: "center", borderTopLeftRadius: "radius.16", borderBottomLeftRadius: "radius.16", cursor: "pointer", onClick: handleOpen, zIndex: "zIndex.2", testId: "feedback-survey", maxWidth: "340px", children: isOpen ? (_jsx(Form, { handleClose: () => setIsOpen(false), mediaID: mediaID })) : (_jsxs(Box, { display: "flex", gap: "spacing.8", alignItems: "center", children: [_jsx(InfoFilledCircleIcon, { color: "color.content.status.information.default" }), _jsx(Box, { as: "span", typography: "typography.body.16.bold", children: t("gallery.floorplans.feedback_survey.not_floorplan") })] })) }));
};
export const Form = ({ handleClose, mediaID }) => {
    const { add: addSnackbar } = useSnackbar();
    const { t } = useI18n();
    const [isLoading, setIsLoading] = useState(false);
    const feedbackSurveyUrl = useBrandUrl({
        endpoint: FEEDBACK_SURVEY_PATH(mediaID),
    });
    const radioItems = [
        { label: t(OptionLabel.Photo), value: OptionValue.Photo },
        {
            label: t(OptionLabel.EnergyCertificate),
            value: OptionValue.EnergyCertificate,
        },
        { label: t(OptionLabel.Logo), value: OptionValue.Logo },
        { label: t(OptionLabel.Map), value: OptionValue.Map },
        {
            label: t(OptionLabel.Document),
            value: OptionValue.Document,
        },
        { label: t(OptionLabel.Other), value: OptionValue.Other },
    ];
    const { control, handleSubmit, formState: { errors }, clearErrors, } = useForm({
        mode: "onSubmit",
    });
    const onSubmit = async (data) => {
        const body = {
            classification: data.option,
        };
        setIsLoading(true);
        try {
            const response = await fetchWithTimeout(feedbackSurveyUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(body),
            });
            if (!response.ok) {
                setIsLoading(false);
                addSnackbar({
                    variant: "error",
                    description: t("gallery.floorplans.feedback_survey.snackbar.error"),
                });
                return;
            }
            setIsLoading(false);
            handleClose();
            addSnackbar({
                variant: "success",
                description: t("gallery.floorplans.feedback_survey.snackbar.success"),
            });
        }
        catch (error) {
            setIsLoading(false);
            addSnackbar({
                variant: "error",
                description: t("gallery.floorplans.feedback_survey.snackbar.error"),
            });
        }
    };
    return (_jsxs(Box, { children: [_jsxs(Box, { display: "flex", justifyContent: "space-between", children: [_jsx(Box, { as: "span", typography: "typography.body.16.bold", children: t("gallery.floorplans.feedback_survey.headline") }), _jsx(Button, { icon: CloseIcon, iconOnly: true, size: "32", variant: "tertiary", onPress: handleClose, marginLeft: "spacing.32", children: "Close" })] }), _jsxs(Box, { as: "form", display: "flex", flexDirection: "column", gap: "spacing.16", onSubmit: handleSubmit(onSubmit), children: [_jsx(Controller, { name: "option", control: control, rules: {
                            required: t("gallery.floorplans.feedback_survey.error"),
                        }, render: ({ field: { onChange } }) => {
                            var _a;
                            return (_jsx(RadioGroup, { items: radioItems, label: "", name: "feedback survey", hasBorder: true, hasNecessityIndicator: false, "aria-label": "Feedback survey", onChange: (selectedValue) => {
                                    onChange(selectedValue);
                                    clearErrors("option");
                                }, error: (_a = errors === null || errors === void 0 ? void 0 : errors.option) === null || _a === void 0 ? void 0 : _a.message, isDisabled: isLoading }));
                        } }), _jsx(Button, { isFullWidth: true, variant: "secondary", type: "submit", isLoading: isLoading, children: t("gallery.floorplans.feedback_survey.submit") })] })] }));
};
